import { SxProps, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { Theme } from "@emotion/react";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import TextFieldComponent from "components/input/TextFieldComponent";
import { MESSAGE } from "constants/validate";
import { useState } from "react";
import { MstItemOptionUpdate, OptionItem } from "../types/option";

interface WheelOptionTableProps {
  labelHeader: DataHeaderType[];
  sx?: SxProps<Theme>;
  data: OptionItem[];
  setData: (data: OptionItem[]) => void;
  onUpdateItem: (data: MstItemOptionUpdate) => Promise<void>;
}

/**
 * Renders a table displaying tire options.
 */
export default function WheelOptionTable({
  labelHeader,
  sx,
  data,
  setData,
  onUpdateItem,
}: WheelOptionTableProps) {
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const onChangeItem = (index: number, key: string, value: string) => {
    const options = [...data];
    options[index] = {
      ...options[index],
      [key]: value,
    };
    setData(options);
  };

  const onBlur = async (index: number, key: string, value: any) => {
    // Only call the update if no errors
    if (Object.keys(errors).length === 0 || Object.values(errors).every((error) => !error)) {
      await onUpdateItem({
        id: data[index].id,
        option_group_cd: data[index].option_group_cd,
        option_name: data[index].option_name,
        sale_price: Number(data[index].sale_price),
        jan_cd: data[index].jan_cd,
      });
    }
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        width: "100%",
        boxShadow: "none",
        borderCollapse: "unset",
        borderSpacing: "1px",
        minWidth: 870
      }}
    >
      <TableHead>
        <TableRowComponent data={labelHeader} type="header" />
      </TableHead>
      <TableBody sx={{ boxShadow: "none" }}>
        {data.map((option, index) => (
          <TableRow key={option.id} sx={{ borderBottom: "none" }}>
            <TableCellComponent sx={{ p: 0, border: 'none' }}>
              <TextFieldComponent
                type="string"
                value={option.jan_cd}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px", textAlign: "center" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-jan_cd`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onBlur={() => onBlur(index, "jan_cd", option.jan_cd)}
                onInputText={(e) => onChangeItem(index, "jan_cd", e.target.value.replace(/[^0-9]/g, ""))}
                validateOption={{
                  minLength: {
                    value: 13,
                    message: "",
                  },
                  numericFormat: {
                    precision: 13,
                    scale: 0,
                    message: "",
                  },
                }}
                isHiddenError
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-jan_cd`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-jan_cd`];
                    setErrors(updatedErrors);
                  }
                }}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
            <TableCellComponent sx={{ p: 0, border: 'none' }}>
              {/* <Tooltip title={errors[`${index}-option_name`] ?? ""} followCursor> */}
              <TextFieldComponent
                value={option.option_name}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-option_name`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onChangeText={(value) => onChangeItem(index, "option_name", value)}
                onBlur={() => onBlur(index, "option_name", option.option_name)}
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  maxLength: {
                    value: 100,
                    message: "",
                  },
                }}
                isHiddenError
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-option_name`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-option_name`];
                    setErrors(updatedErrors);
                  }
                }}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
            <TableCellComponent sx={{ p: 0, border: 'none' }}>
              <TextFieldComponent
                type="string"
                value={option.sale_price}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px", textAlign: "center" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-sale_price`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onBlur={() => onBlur(index, "sale_price", option.sale_price)}
                // onBlur={() => onUpdateItem && onUpdateItem(index, "maker_name")}
                onInputText={(e) => onChangeItem(index, "sale_price", e.target.value.replace(/[^0-9]/g, ""))}
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  minLength: {
                    value: 3,
                    message: "",
                  },
                  numericFormat: {
                    precision: 9,
                    scale: 0,
                    message: "",
                  },
                }}
                isHiddenError
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-sale_price`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-sale_price`];
                    setErrors(updatedErrors);
                  }
                }}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
